import * as THREE from 'three';
import { EG, PLR_GREEN } from '../enemy-parts';
import { stdMat } from '../../render/scene';
export { TINT } from '../enemy-parts';
/* ============================================================
   BOSS 的共用技法（移植自 `enemy_sample_ani.html` §3 / §6）
   ============================================================

   由 `tools/port-bosses.mjs` 產生，**不要手改**。

   ## 為什麼 BOSS 不走小怪那條路

   小怪是「part 表 + `fn(P,o)`」餵進 `InstancedMesh`（沒有階層，關節只能單節）。
   BOSS 場上一次只有一隻，付得起**真的階層 rig** —— 巢狀骨段、IK、
   獨立追瞄的砲塔、可破壞部位。所以這裡是一整套 `THREE.Mesh` 的技法。

   ## 材質：BOSS 用**未染色**的基準色

   小怪的裝甲會被威脅色染一層（`enemyPalette(tint)`），BOSS 不染 ——
   原型就是這樣，而且 BOSS 身上的發光件夠多夠大，四色語彙由它們講。
   `armorE`（亮一階）留給精英，BOSS 用 `armor`。

   ## `userData.bone` 是整套的樞紐

   標了 `bone` 的 Group ＝「一段獨立剛體」。`mergeBones()` 只在這些節點上
   動刀（骨段內的靜態裝甲依材質烘成一顆，draw call 砍掉六成），
   而**死亡解體也認同一份標記** —— 一份標註，兩個用途。 */

/** 幾何池：與小怪共用同一份（都開了 vertexColors，見 `enemy-parts.ts`）。 */
export var G = EG;
export var HP = Math.PI / 2;
export var K_BEV = 1 / Math.cos(Math.PI / 8);

/** BOSS 的基準材質。**不吃威脅色染色**（理由見檔頭）。 */
export var M: any = {
  armor: stdMat(0x59616f, { metal: 0.52, rough: 0.46, flat: true, vcol: true }),
  armorE: stdMat(0x8d99b0, { metal: 0.56, rough: 0.40, flat: true, vcol: true }),
  dark: stdMat(0x161c27, { metal: 0.58, rough: 0.55, flat: true, vcol: true }),
  trim: stdMat(0x090c13, { metal: 0.40, rough: 0.64, flat: true, vcol: true }),
  /* 最終 BOSS 的裸金屬（原型 2026-08-03 新增）。比一般敵人亮、比玩家暗、
     而且降飽和 —— 量產不上漆的東西，反光讀起來是金屬本色不是塗裝。
     明度仍然明確低於玩家的 0xc6d0de，規格 §3 規則 1 沒有被打破。 */
  armorF: stdMat(0x76808f, { metal: 0.62, rough: 0.34, flat: true, vcol: true }),
  /* 深層 BOSS 唯一的新材質：完全不受光，讀作「這裡被挖掉了」而不是
     「這裡有個黑色零件」。它不是第五個威脅色。 */
  void: new THREE.MeshBasicMaterial({ color: 0x04050a, vertexColors: true }),

  /* **玩家的材質。** REQUIEM 安魂曲是用玩家機甲的殘骸堆出來的 ——
     規格 §3 規則 3「綠色只屬於玩家」被刻意打破一次，而且只有那一次，
     代價是它必須是最終 BOSS，而且必須用「這些真的是玩家機甲」來成立。
     所以那隻的殘骸要吃玩家的亮灰、暖調與綠面罩，不是敵人的冷灰。

     ⚠️ 這裡是**另外一份實例**，不是 render/materials.ts 的 MAT_P。
     B2 把 REQUIEM 接進戰場時要考慮改指同一份 —— 否則玩家換色時
     殘骸不會跟著換，「那是我的同型機」這件事就會脫鉤。 */
  pArm: stdMat(0xc6d0de, { metal: 0.55, rough: 0.42, flat: true, vcol: true }),
  pDark: stdMat(0x353d4c, { metal: 0.58, rough: 0.50, flat: true, vcol: true }),
  pTrim: stdMat(0x141a24, { metal: 0.40, rough: 0.62, flat: true, vcol: true })
};

M.pGlow = glowMat(PLR_GREEN, 2.4);

/** 發光材質。`userData.base` 存基礎強度 —— 動畫層寫**絕對值**不做累加，
    否則同一顆材質被兩個地方調整就會互相疊加成越來越亮。 */
export function glowMat(hex, k) {
  var m = stdMat(0x090b10, { em: hex, ei: k, metal: 0.0, rough: 0.35, flat: true, vcol: true });
  m.userData.base = k;
  return m;
}

/* ---------- 幾何技法（原型 §3，逐字） ---------- */

export function mesh(geo, mat, s, p, r?){
  var m = new THREE.Mesh(geo, mat);
  m.scale.set(s[0], s[1], s[2]);
  m.position.set(p[0], p[1], p[2]);
  if (r) m.rotation.set(r[0], r[1], r[2]);
  m.castShadow = true; m.receiveShadow = true;
  return m;
}
export function bevel(mat, w, h, d, p, r?){ return mesh(G.bev, mat, [w*K_BEV, h, d*K_BEV], p, r); }
export function plate(mat, w, h, d, p, tilt?, yaw?, roll?){
  return mesh(G.box, mat, [w, h, d], p, [tilt === undefined ? 0.16 : tilt, yaw || 0, roll || 0]);
}
export function rib(mat, w, h, d, p, tilt?, yaw?, roll?){
  return mesh(G.bev, mat, [w*K_BEV, h, d*K_BEV], p, [tilt||0, yaw||0, roll||0]);
}
export function seam(w, h, d, p, tilt?, yaw?, roll?){
  return mesh(G.box, M.trim, [w, h, d], p, [tilt||0, yaw||0, roll||0]);
}
/* 纜束：兩節折線細管，用直的 primitive 湊出垂下來的線束。
   機體檔有、敵人檔一直沒移植 —— 這是「精緻」最便宜的一項：
   一條垂在關節縫外面的線，就把實心塊變成有內部的機構。 */
export function cable(p, len, mat?){
  var g = new THREE.Group();
  g.position.set(p[0], p[1], p[2]);
  g.add(mesh(G.cyl, mat || M.trim, [len*0.075, len,      len*0.075],
             [0, -len*0.44,  len*0.16], [ 0.58,0,0]));
  g.add(mesh(G.cyl, mat || M.trim, [len*0.075, len*0.86, len*0.075],
             [0, -len*1.14,  len*0.30], [-0.30,0,0]));
  return g;
}
/* 兩段式肢體：上段厚、下段收。
   這是機體檔跟敵人檔差最多的一件事 —— 我的敵人四肢一直是單一圓柱，
   而單一圓柱在任何尺寸下都讀作「棍子」，不是「有肌肉分佈的肢體」。
   一根換兩根，primitive +1，質感差一級。 */
export function limb2(mat, w, len, p, r?){
  var g = new THREE.Group();
  g.position.set(p[0], p[1], p[2]);
  if (r) g.rotation.set(r[0], r[1], r[2]);
  g.add(bevel(mat, w,        len*0.52, w*0.98,  [0, -len*0.26, 0]));   // 上段：厚
  g.add(bevel(mat, w*0.72,   len*0.50, w*0.72,  [0, -len*0.75, 0]));   // 下段：收
  return g;
}
export function joint(r, p, cap?){
  var g = new THREE.Group();
  g.position.set(p[0], p[1], p[2]);
  g.add(mesh(G.cyl, M.trim, [r*2.1, r*(cap?2.5:1.5), r*2.1], [0,0,0], [0,0,HP]));
  g.add(mesh(G.oct, M.dark, [r*1.5, r*1.5, r*1.5], [0,0,0]));
  if (cap){
    g.add(mesh(G.cyl, M.dark, [r*1.35, r*0.45, r*1.35], [ r*1.32,0,0], [0,0,HP]));
    g.add(mesh(G.cyl, M.dark, [r*1.35, r*0.45, r*1.35], [-r*1.32,0,0], [0,0,HP]));
  }
  return g;
}
export function ram(p, len, a, b, rot?){
  var g = new THREE.Group();
  g.position.set(p[0], p[1], p[2]);
  if (rot) g.rotation.set(rot[0], rot[1], rot[2]);
  g.add(mesh(G.cyl, M.trim,  [a, len*0.62, a], [0,  len*0.19, 0]));
  g.add(mesh(G.cyl, M.armor, [b, len*0.52, b], [0, -len*0.24, 0]));
  return g;
}

/* ── §4 動畫數學 ─────────────────────────────────────────────── */
export function clamp(v,a,b){ return v<a?a:(v>b?b:v); }
export function wrap(a){ while(a> Math.PI) a-=Math.PI * 2; while(a<-Math.PI) a+=Math.PI * 2; return a; }
export function damp(c,t,l,dt){ return t + (c-t)*Math.exp(-l*dt); }
export function angTo(cur, tgt, step){
  var d = wrap(tgt - cur);
  return cur + (Math.abs(d) < step ? d : (d>0?step:-step));
}

/* ---------- BOSS 專用（原型 §6） ---------- */

export var LB1 = 0.90, LB2 = 0.86, LBF = 0.16;      // BOSS-01 的大腿／小腿／踝高
export function legIK(dy, dz){
  var d = clamp(Math.sqrt(dy*dy + dz*dz), Math.abs(LB1-LB2)+0.03, LB1+LB2-0.03);
  var b = Math.atan2(-dz, -dy);
  var a = Math.acos(clamp((LB1*LB1 + d*d - LB2*LB2)/(2*LB1*d), -1, 1));
  var c = Math.acos(clamp((LB2*LB2 + d*d - LB1*LB1)/(2*LB2*d), -1, 1));
  return {hip:b-a, knee:a+c};
}

/* ══════════════════════════════════════════════════════════════
   BOSS 03–07 —— 每隻兩階段。二階段一律「造型改變講機制」：
   殼會開、刃會掉、塔會倒、蛇會散。玩家不用讀 UI 就知道規則變了。
   ══════════════════════════════════════════════════════════════ */
/* 三條血量的形狀端表示：s1 = 已進入二階段、s2 = 已進入三階段，
   兩者都是 0→1 的平滑值。造型一律寫成 s1/s2 的線性組合，
   這樣「回上一階段」會自動變成反向動畫，不用另外寫。 */
export function stages(rig, o, dt, k?){
  k = k || 2.4;
  rig.s1 = damp(rig.s1 === undefined ? 0 : rig.s1, o.stg >= 1 ? 1 : 0, k, dt);
  rig.s2 = damp(rig.s2 === undefined ? 0 : rig.s2, o.stg >= 2 ? 1 : 0, k, dt);
  rig.s3 = damp(rig.s3 === undefined ? 0 : rig.s3, o.stg >= 3 ? 1 : 0, k, dt);  // 最終 BOSS 的第四條
  return rig;
}
export function boneG(parent, x, y, z){
  var g = new THREE.Group(); g.userData.bone = 1;
  g.position.set(x||0, y||0, z||0); parent.add(g); return g;
}

/* ---------- 骨段合併（原型 §9） ---------- */


/* 依材質合併「不會動的部件」—— 精英是真 rig，但沒必要一個部件一個 draw call。 */
export function mergeGeoms(list){
  var total = 0, i;
  for (i = 0; i < list.length; i++) total += list[i].attributes.position.count;
  var pos = new Float32Array(total*3), nor = new Float32Array(total*3),
      col = new Float32Array(total*3), off = 0;
  for (i = 0; i < list.length; i++){
    pos.set(list[i].attributes.position.array, off*3);
    nor.set(list[i].attributes.normal.array,   off*3);
    off += list[i].attributes.position.count;
    list[i].dispose();
  }
  for (i = 0; i < total*3; i++) col[i] = 1;      // 材質開了 vertexColors，一定要補
  var g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal',   new THREE.BufferAttribute(nor, 3));
  g.setAttribute('color',    new THREE.BufferAttribute(col, 3));
  return g;
}
/** 骨段合併：以 userData.bone 為邊界，把該骨段底下（不含巢狀骨段）的 mesh
    依材質烘成一顆。骨段之間照樣自由旋轉，所以 IK 與砲塔追瞄都不受影響。
    這是機體那支 mergeBones 原封不動搬過來，BOSS 的 draw call 直接砍掉六成。 */
export function mergeBones(root){
  root.updateMatrixWorld(true);
  var bones = [];
  root.traverse(function(o){ if (o.userData.bone) bones.push(o); });
  bones.forEach(function(bone){
    var inv = new THREE.Matrix4().copy(bone.matrixWorld).invert();
    var kill = [], mats = [], lists = [];
    (function walk(node){
      for (var i = 0; i < node.children.length; i++){
        var c = node.children[i];
        if (c.userData.bone) continue;                 // 巢狀骨段自己處理
        if (c.isMesh && !c.userData.noMerge){
          var g = c.geometry.clone();
          if (g.index){ var ni = g.toNonIndexed(); g.dispose(); g = ni; }
          g.applyMatrix4(new THREE.Matrix4().multiplyMatrices(inv, c.matrixWorld));
          var k = mats.indexOf(c.material);
          if (k < 0){ mats.push(c.material); lists.push([]); k = mats.length - 1; }
          lists[k].push(g); kill.push(c);
        }
        walk(c);
      }
    })(bone);
    for (var i = 0; i < kill.length; i++) kill[i].parent.remove(kill[i]);
    for (i = 0; i < mats.length; i++){
      var m = new THREE.Mesh(mergeGeoms(lists[i]), mats[i]);
      m.castShadow = true; m.receiveShadow = true;
      bone.add(m);
    }
  });
  return root;
}
